'use client'

import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, useState } from 'react'
import { IconClose } from '../icons/icon-close'

const tagVariants = cva(
  'inline-flex items-center rounded-lg py-2 px-3 whitespace-nowrap text-sm font-sans text-neutral-600 leading-[18px]',
  {
    variants: {
      variant: {
        default: 'border border-neutral-200',
      },
      size: {
        small: 'h-[26px] py-1 px-2',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

export interface TagProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof tagVariants> {
  closable?: boolean
  title: string
  icon?: React.ReactNode
  size?: 'small'
  onClose?: () => void
}

const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    { className, variant, title, icon, closable = false, size, ...props },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(true)
    const [isDestroyed, setIsDestroyed] = useState(false)

    const handleClose = () => {
      setIsVisible(false)
      setTimeout(() => {
        setIsDestroyed(true)
        props?.onClose?.()
      }, 200)
    }

    if (isDestroyed) return null

    return (
      <div
        ref={ref}
        className={cn(
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0',
          'transition-opacity duration-200 ease-in-out',
          tagVariants({ variant, size }),
          className
        )}
        {...props}
      >
        <div className='flex items-center'>
          {icon && (
            <span
              className={cn(
                'w-h mr-1 flex h-5 items-center justify-center',
                size === 'small' && 'h-[15px] w-[15px]'
              )}
            >
              {icon}
            </span>
          )}
          {title}
        </div>
        {closable && (
          <IconClose
            className={cn(
              'ml-3 h-3.5 w-3.5 cursor-pointer text-neutral-400 hover:opacity-70',
              size === 'small' && 'ml-2 h-2.5 w-2.5'
            )}
            onClick={handleClose}
          />
        )}
      </div>
    )
  }
)

Tag.displayName = 'Tag'

export { Tag }
